import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ShieldCheck } from "lucide-react";
import { SITE } from "@/data/site";

const LINKS = [
  { label: "Services", type: "section", id: "services" },
  { label: "Pricing", type: "section", id: "pricing" },
  { label: "Service Areas", type: "section", id: "areas" },
  { label: "Reviews", type: "section", id: "reviews" },
  { label: "VIN Decoder", type: "route", to: "/free-vin-decoder" },
  { label: "Blog", type: "route", to: "/blog" },
  { label: "FAQ", type: "route", to: "/faq" },
  { label: "Contact", type: "route", to: "/contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (id === "book") {
      if (pathname === "/book") {
        document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/book");
      }
      return;
    }
    if (pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const handleLink = (l) => {
    if (l.type === "section") goSection(l.id);
    else { setOpen(false); navigate(l.to); }
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -70, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}
      className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? "shadow-[0_2px_20px_rgba(10,37,145,0.07)] border-b border-gray-100" : "border-b border-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-lg bg-brand flex items-center justify-center"><ShieldCheck size={19} className="text-white" /></span>
          <span className="text-left leading-none">
            <span className="block font-display font-bold text-ink text-lg tracking-tight">The VIN Verifier CA</span>
            <span className="hidden sm:block text-[10px] text-slate2 tracking-wide mt-0.5">California Licensed Vehicle Verifier</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-4">
          {LINKS.map((l) => l.type === "route" ? (
            <Link key={l.label} to={l.to} data-testid={`nav-link-${l.to.slice(1)}`} onClick={() => setOpen(false)} className="text-slate2 text-sm font-medium hover:text-brand transition-colors">
              {l.label}
            </Link>
          ) : (
            <a key={l.label} href={`/#${l.id}`} data-testid={`nav-link-${l.id}`} onClick={(event) => { event.preventDefault(); handleLink(l); }} className="text-slate2 text-sm font-medium hover:text-brand transition-colors">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <a data-testid="nav-call-btn" href={SITE.phoneHref} className="hidden sm:flex items-center gap-2 text-brand font-semibold text-sm hover:opacity-80 transition-opacity"><Phone size={16} /> {SITE.phone}</a>
          <Link to="/book" data-testid="nav-book-btn" className="btn-accent hidden sm:inline-flex rounded-full px-5 py-2.5 text-sm font-semibold">Book Appointment</Link>
          <a href={SITE.phoneHref} className="sm:hidden h-10 w-10 rounded-full bg-brand text-white flex items-center justify-center" aria-label="Call now"><Phone size={17} /></a>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen((o) => !o)} className="xl:hidden text-ink p-2" aria-label="Toggle menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="mobile-menu" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="xl:hidden overflow-hidden bg-white border-t border-gray-100">
            <div className="px-5 py-5 flex flex-col gap-1">
              {LINKS.map((l) => l.type === "route" ? (
                <Link key={l.label} to={l.to} data-testid={`mobile-link-${l.to.slice(1)}`} onClick={() => setOpen(false)} className="text-left text-ink font-medium py-2.5 border-b border-gray-50">{l.label}</Link>
              ) : (
                <a key={l.label} href={`/#${l.id}`} data-testid={`mobile-link-${l.id}`} onClick={(event) => { event.preventDefault(); handleLink(l); }} className="text-left text-ink font-medium py-2.5 border-b border-gray-50">{l.label}</a>
              ))}
              <Link to="/book" onClick={() => setOpen(false)} className="btn-accent rounded-full px-5 py-3 text-sm font-semibold mt-3 text-center">Book Appointment</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
